import SelectButton from '@/components/common/select-button';
import Select from '@/components/form/select';
import {
	ALLOCATION_METHOD_OPTIONS,
	ASSIGNMENT_SETTING_USED_OPTIONS,
	AssignmentSettingUsedEnum,
	OPTION_SELECT,
	RANGE_AGE_OPTIONS,
	REGEX,
	ROUTERS,
} from '@/constant';
import { useRowSelection } from '@/hooks';
import { ICommonApplicant } from '@/services';
import { TCommonApplicantParams } from '@/stores';
import {
	dayjs,
	getFieldDateProps,
	getFieldInputProps,
	getFieldRadioProps,
	getFieldSelectProps,
	getNestedFieldInputProps,
	getNestedFieldSelectProps,
	insertObjectIfV2,
	SUPPORT_FORMAT_DATE_LIST,
} from '@/utils';
import { memo } from 'react';

import { DefaultOptionType } from 'antd/es/select';
import { TableRowSelection } from 'antd/es/table/interface';

import {
	DatePicker,
	GroupInput,
	Radio,
	SelectFranchiseModalButton,
	SelectMasterInfo,
	Textarea,
} from '../../..';
import Input from '../../../form/input';
import { MyFormikType } from '../../define-assignment.config';
import { assignmentInfoLogic } from './assignment-info.logic';

export type AssignmentInfoProps = {
	formik: MyFormikType;
	fetchData?: (params: TCommonApplicantParams) => void;
	params?: TCommonApplicantParams;
	rowSelection: ReturnType<
		typeof useRowSelection<
			TableRowSelection<ICommonApplicant>,
			ICommonApplicant
		>
	>;
	applicantPublicMainFranchiseId?: number;
	programCommonList: {
		fetching: boolean;
		data: unknown[];
	};
	programList: DefaultOptionType[];
};

const AssignmentInfo = (props: AssignmentInfoProps) => {
	const { formik } = props;

	const {
		programOptions,
		onChangeRadio,
		handleDateChange,
		onChangeProgram,
		onSaveFranchise,
		onResetFranchise,
		onChangeAssignmentSetting,
		getNestedAdditionalValues,
		districtSelected,
	} = assignmentInfoLogic(props);

	const { values } = formik;
	const { isDetail } = values;

	return (
		<div className="">
			<div className="text-font2 font-bold text-[16px] leading-[20px] border-b p-5">
				배정 정보
			</div>

			<div className="p-5 grid gap-y-6">
				<div className="grid grid-cols-2 gap-x-[192px] gap-y-6 2xl:w-2/3 items-start">
					<div className="grid gap-y-6">
						<GroupInput
							labelClassName="w-[138px] flex-shrink-0"
							label="배정 사업장"
							required
						>
							<SelectButton
								hyperLinkProps={{
									params: {
										pathname: ROUTERS.FR1_03,
										id: formik.values.franchiseId,
									},
									className: 'flex items-center',
								}}
								text={formik.values.franchiseName}
								action={
									<SelectFranchiseModalButton
										label="조회"
										onSaveFranchise={onSaveFranchise}
									/>
								}
								onDelete={onResetFranchise}
								disabled={isDetail}
							/>
						</GroupInput>

						<GroupInput labelClassName="w-[138px] flex-shrink-0" label="읍/면">
							<span className="w-full text-[13px] xl:text-[13px] xl:h-[30px] text-font2 py-[5px]">
								{districtSelected?.label || '-'}
							</span>
						</GroupInput>

						<SelectMasterInfo
							{...getFieldSelectProps(formik, 'programId', {
								defaultValue: '',
							})}
							options={programOptions}
							onChange={onChangeProgram}
							labelClassName="w-[138px] flex-shrink-0"
							label="프로그램"
							disabled={isDetail}
						/>
					</div>

					<div className="grid gap-y-6">
						<Input
							{...getFieldInputProps(formik, 'requestApplicant', {
								regex: REGEX.ONLY_NUMBER,
							})}
							maxLength={3}
							labelClassName="w-[138px] flex-shrink-0"
							label="요청 인원"
							require
							disabled={isDetail}
						/>

						<GroupInput
							labelClassName="w-[138px] flex-shrink-0"
							label="배정 기간"
						>
							<DatePicker
								{...getFieldDateProps(formik, 'startDate', {
									format: true,
								})}
								onChange={handleDateChange('startDate')}
								{...insertObjectIfV2(formik.values.endDate, {
									maxDate: dayjs(
										formik.values.endDate,
										SUPPORT_FORMAT_DATE_LIST
									),
								})}
								disabled={isDetail}
							/>
							<span className="text-2xl">-</span>
							<DatePicker
								{...getFieldDateProps(formik, 'endDate', {
									format: true,
								})}
								onChange={handleDateChange('endDate')}
								{...insertObjectIfV2(formik.values.startDate, {
									minDate: dayjs(
										formik.values.startDate,
										SUPPORT_FORMAT_DATE_LIST
									),
								})}
								disabled={isDetail}
							/>
						</GroupInput>
					</div>
				</div>

				<Textarea
					{...getFieldInputProps(formik, 'memo')}
					labelClassName="w-[138px] flex-shrink-0"
					containerClassName="items-start"
					label="특이사항"
					maxLength={500}
				/>

				<GroupInput
					labelClassName="w-[138px] flex-shrink-0"
					label="자동 배정 설정"
					required
				>
					<Radio
						{...getFieldRadioProps(formik, 'isUsed')}
						options={ASSIGNMENT_SETTING_USED_OPTIONS}
						containerClassName="gap-2"
						onChange={onChangeAssignmentSetting}
					/>

					<div className="flex items-end gap-4">
						<div className="flex items-center gap-4">
							<div className="flex items-center gap-2">
								<Input
									{...getNestedFieldInputProps(
										formik,
										'assignSetting.maleRequest',
										{
											regex: REGEX.ONLY_NUMBER,
											getAdditionalValues: getNestedAdditionalValues(
												'assignSetting.maleRequest'
											),
										}
									)}
									maxLength={3}
									label="남자:"
									labelClassName="min-w-[30px]"
									inputClassName="w-[70px]"
									disabled={
										formik.values.isUsed !== AssignmentSettingUsedEnum.USED
									}
								/>
								<span className="text-[13px] font-medium text-font2">명</span>
							</div>

							<Select
								{...getNestedFieldSelectProps(
									formik,
									'assignSetting.maleAgeRange',
									{
										getAdditionalValues: getNestedAdditionalValues(
											'assignSetting.maleAgeRange'
										),
									}
								)}
								options={[OPTION_SELECT, ...RANGE_AGE_OPTIONS]}
								// containerClassName="mr-[5px]"
								label="나이"
								labelClassName="min-w-[30px]"
								disabled={
									formik.values.isUsed !== AssignmentSettingUsedEnum.USED
								}
							/>
						</div>
						<span>,</span>

						<div className="flex items-center gap-4">
							<div className="flex items-center gap-2">
								<Input
									{...getNestedFieldInputProps(
										formik,
										'assignSetting.femaleRequest',
										{
											regex: REGEX.ONLY_NUMBER,
											getAdditionalValues: getNestedAdditionalValues(
												'assignSetting.femaleRequest'
											),
										}
									)}
									maxLength={3}
									label="여자:"
									labelClassName="min-w-[30px]"
									inputClassName="w-[70px]"
									disabled={
										formik.values.isUsed !== AssignmentSettingUsedEnum.USED
									}
								/>
								<span className="text-[13px] font-medium text-font2">명</span>
							</div>

							<Select
								{...getNestedFieldSelectProps(
									formik,
									'assignSetting.femaleAgeRange',
									{
										getAdditionalValues: getNestedAdditionalValues(
											'assignSetting.femaleAgeRange'
										),
									}
								)}
								options={[OPTION_SELECT, ...RANGE_AGE_OPTIONS]}
								// containerClassName="mr-[5px]"
								label="나이"
								labelClassName="min-w-[30px]"
								disabled={
									formik.values.isUsed !== AssignmentSettingUsedEnum.USED
								}
							/>
						</div>
					</div>
				</GroupInput>

				<Radio
					{...getFieldRadioProps(formik, 'assignmentMethod')}
					options={ALLOCATION_METHOD_OPTIONS}
					onChange={onChangeRadio}
					labelClassName="w-[138px] flex-shrink-0"
					containerClassName="gap-2"
					inputClassName=" space-y-2"
					label="배정 방식"
					require
				/>
			</div>
		</div>
	);
};

export default memo(AssignmentInfo);
